import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  Loader2, 
  AlertCircle,
  Copy,
  CheckCircle,
  Mail,
  ArrowRight,
  FileText,
  Eye,
  MessageSquare,
  Repeat
} from 'lucide-react';
import { streamOpenAIResponse } from '../../lib/openai';

interface EmailDraft {
  subject: string;
  preview: string;
  body: string;
}

const EmailDraftGenerator = () => {
  const [postCaption, setPostCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailDraft, setEmailDraft] = useState<EmailDraft | null>(null);
  const [copied, setCopied] = useState<'subject' | 'preview' | 'body' | 'all' | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!postCaption.trim()) {
      setError('Please enter a social media post to convert');
      return;
    }

    setLoading(true);
    setError(null);
    setEmailDraft(null);

    try {
      const prompt = `You are a direct-response copywriter; OriginalPost="${postCaption}"; produce ONLY JSON { "subject": (≤60 chars), "preview": (≤90 chars), "body": "para1\\npara2\\npara3" } totaling about 75-90 words; output nothing else.`;

      let responseText = '';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          try {
            // Clean the response text to handle markdown code blocks
            let cleanedResponse = responseText;
            if (cleanedResponse.includes('```json')) {
              cleanedResponse = cleanedResponse.replace(/```json\n|\n```/g, '');
            } else if (cleanedResponse.includes('```')) {
              cleanedResponse = cleanedResponse.replace(/```\n|\n```/g, '');
            }
            
            const parsedData = JSON.parse(cleanedResponse);
            if (parsedData.subject && parsedData.preview && parsedData.body) {
              setEmailDraft(parsedData);
            }
          } catch (e) {
            // Ignore parsing errors during streaming
          }
        },
        (error) => {
          throw error;
        }
      );
    } catch (err) {
      setError('Failed to generate email draft. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (type: 'subject' | 'preview' | 'body' | 'all') => {
    if (!emailDraft) return;
    
    let textToCopy = '';
    
    switch (type) {
      case 'subject':
        textToCopy = emailDraft.subject;
        break;
      case 'preview':
        textToCopy = emailDraft.preview;
        break;
      case 'body':
        textToCopy = emailDraft.body;
        break;
      case 'all':
        textToCopy = `Subject: ${emailDraft.subject}\n\nPreview: ${emailDraft.preview}\n\n${emailDraft.body}`;
        break;
    }
    
    navigator.clipboard.writeText(textToCopy);
    setCopied(type);
    
    setTimeout(() => {
      setCopied(null);
    }, 2000);
  };

  const formatEmailBody = (body: string) => {
    return body.split('\\n').join('\n');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="flex items-center space-x-4">
        <Link to="/ai-tools" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Email Draft Generator</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Convert social media posts into professional email drafts
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Generate Email Draft</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="postCaption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Social Media Post
            </label>
            <textarea
              id="postCaption"
              value={postCaption}
              onChange={(e) => setPostCaption(e.target.value)}
              rows={6}
              placeholder="Paste your social media post or caption here to convert it into an email draft..."
              className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The AI will transform your social post into a professional email with subject line, preview text, and body content.
            </p>
          </div>

          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading || !postCaption.trim()}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                loading || !postCaption.trim()
                  ? 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Mail className="h-5 w-5" />
                  <span>Generate Email</span>
                </>
              )}
            </motion.button>
          </div>
        </form>
      </div>

      {/* Results */}
      {emailDraft && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Mail className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
              Email Draft
            </h2>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleCopy('all')}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
            >
              {copied === 'all' ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>Copied All</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  <span>Copy All</span>
                </>
              )}
            </motion.button>
          </div>

          <div className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-100 dark:border-dark-700 shadow-sm">
            {/* Subject Line */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                  <FileText className="h-4 w-4 mr-1.5 text-indigo-600 dark:text-indigo-400" />
                  Subject Line <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({emailDraft.subject.length}/60 chars)</span>
                </h3>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleCopy('subject')}
                  className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
                >
                  {copied === 'subject' ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </motion.button>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                <p className="text-gray-900 dark:text-white font-medium">{emailDraft.subject}</p>
              </div>
            </div>

            {/* Preview Text */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                  <Eye className="h-4 w-4 mr-1.5 text-indigo-600 dark:text-indigo-400" />
                  Preview Text <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({emailDraft.preview.length}/90 chars)</span>
                </h3>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleCopy('preview')}
                  className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
                >
                  {copied === 'preview' ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </motion.button>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                <p className="text-gray-600 dark:text-gray-300 italic">{emailDraft.preview}</p>
              </div>
            </div>

            {/* Email Body */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                  <MessageSquare className="h-4 w-4 mr-1.5 text-indigo-600 dark:text-indigo-400" />
                  Email Body
                </h3>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleCopy('body')}
                  className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
                >
                  {copied === 'body' ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </motion.button>
              </div>
              <div className="p-4 bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
                <div className="prose dark:prose-invert max-w-none">
                  {formatEmailBody(emailDraft.body).split('\n').map((paragraph, index) => (
                    <p key={index} className="mb-4 last:mb-0">{paragraph}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tips Section */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-3 mb-4">
          <Mail className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Email Marketing Tips</h3>
        </div>
        <ul className="space-y-3 text-indigo-100">
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Subject lines under 60 characters have higher open rates</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Preview text is visible in most email clients before opening</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Keep paragraphs short and scannable for better engagement</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Always include a clear call-to-action in your email body</span>
          </li>
        </ul>
      </div>

      {/* Repurposing Tips */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-100 dark:border-blue-800/30">
        <div className="flex items-center space-x-4 mb-4">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
            <Repeat className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-100">Content Repurposing</h3>
            <p className="text-blue-800 dark:text-blue-200">Maximize your content across channels</p>
          </div>
        </div>
        <p className="text-blue-700 dark:text-blue-300 mb-4">
          This tool helps you repurpose your social media content for email marketing. Here are some other ways to repurpose your content:
        </p>
        <ul className="space-y-2 text-blue-700 dark:text-blue-300">
          <li className="flex items-start space-x-2">
            <span className="text-blue-600 dark:text-blue-400 mr-1">•</span>
            <span>Turn long-form blog posts into a series of social media posts</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-blue-600 dark:text-blue-400 mr-1">•</span>
            <span>Convert video content into blog articles with screenshots</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-blue-600 dark:text-blue-400 mr-1">•</span>
            <span>Transform podcast episodes into written content pieces</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-blue-600 dark:text-blue-400 mr-1">•</span>
            <span>Use customer testimonials across multiple marketing channels</span>
          </li>
        </ul>
      </div>
    </motion.div>
  );
};

export default EmailDraftGenerator;